/******************************************************************************
 * PhotoCategoryListItem: table row for a photo category.
 * The work-order type labels are resolved client-side (the response carries ids
 * only) and passed in via `typeLabels`.
 *****************************************************************************/

/* Imports */
import { t } from "i18next";
import { Typography } from "@material-tailwind/react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import PhotoCategoryAdminActions from "./PhotoCategoryAdminActions";
/***/

/**
* PhotoCategoryListItem: display a photo category row
* @param category - category data
* @param typeLabels - resolved work-order type labels
* @param onChange - reload callback
* @param isLast - is last item
* @param isReordering - if reordering mode is active
* @param index - display index/order
*/
export default function PhotoCategoryListItem({
  category,
  typeLabels,
  onChange,
  isLast = false,
  isReordering = false,
  index
}: {
  category: EasyTech.PhotoCategory,
  typeLabels: string[],
  onChange: () => void,
  isLast?: boolean,
  isReordering?: boolean,
  index?: number
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({
    id: category.id || "",
    disabled: !isReordering
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`page-list-item ${isLast && "last"} ${isReordering ? "cursor-move relative group" : ""}`}
      {...attributes}
      {...listeners}
    >
      <td>
        <div className="flex gap-2 items-center">
          {isReordering && (
            <div>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-gray-400">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
              </svg>
            </div>
          )}
          <Typography variant="small" color="blue-gray">
            {index || (category.order !== undefined ? category.order : "-")}
          </Typography>
        </div>
      </td>

      <td>
        <div className="flex gap-2 items-center">
          <Typography>{category.label}</Typography>
        </div>
      </td>

      <td>
        <div className="flex flex-wrap gap-1">
          {typeLabels.length === 0
            ? <Typography className="text-gray-400">-</Typography>
            : typeLabels.map((label, i) => (
              <span key={i} className="inline-block rounded-full bg-blue-gray-100 px-2 py-0.5 text-xs">{label}</span>
            ))}
        </div>
      </td>

      <td>
        <div className="flex gap-2 items-center">
          <Typography>{category.minPhotos ?? "-"}</Typography>
        </div>
      </td>

      <td>
        <div className="flex gap-2 items-center">
          <Typography>{category.maxPhotos ?? "-"}</Typography>
        </div>
      </td>

      <td>
        <div className="flex gap-2 items-center">
          <Typography>{t(`settings:photoCategories.applicationField.${category.applicationField || "BOTH"}`)}</Typography>
        </div>
      </td>

      <td>
        <div className="flex gap-2 items-center">
          <Typography>{category.enabled ? "Yes" : "No"}</Typography>
        </div>
      </td>

      {!isReordering && (
        <td><PhotoCategoryAdminActions category={category} onChange={onChange} /></td>
      )}
    </tr>
  );
}
/***/
